import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { SCORED_QUESTIONS, TOTAL_QUESTIONS } from './scoring';
import { useScore } from './ScoreContext';
import { createScoreResultUrl } from './scoreResultUrl';
import { trackScoreComplete } from '../lib/analytics';
import { Cta } from './Cta';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isComplete,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const advancingRef = useRef(false);
  const advanceTimerRef = useRef<number | null>(null);
  const reduceMotion = useReducedMotion();

  // Sync to the first unanswered question only when the panel opens.
  // Do NOT re-sync when answers change — that races the post-answer
  // auto-advance and skips questions (commonly Q2).
  useEffect(() => {
    if (!isDiagnosticOpen) return;
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    setCurrentIndex(index === -1 ? TOTAL_QUESTIONS - 1 : index);
    // intentionally only when open state flips to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isDiagnosticOpen) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (!dialog.open) {
        dialog.showModal();
      }
      // Move focus into the dialog; native modal keeps Tab within it.
      window.requestAnimationFrame(() => {
        dialog.focus();
      });
    } else if (dialog.open) {
      dialog.close();
    }
    if (!isDiagnosticOpen) {
      if (advanceTimerRef.current !== null) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
      advancingRef.current = false;
    }
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !isDiagnosticOpen) return;
    const focusables = () => [...dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof Node && !dialog.contains(e.target)) {
        const list = focusables();
        (list[0] || dialog).focus();
      }
    };
    dialog.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [isDiagnosticOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      closeDiagnostic();
      const opener = openerRef.current;
      openerRef.current = null;
      window.requestAnimationFrame(() => {
        opener?.focus?.();
      });
    };
    dialog.addEventListener('close', onClose);
    return () => dialog.removeEventListener('close', onClose);
  }, [closeDiagnostic]);
  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    advancingRef.current = false;
  };
  useEffect(() => {
    // Unlock after the question index settles (advance, Back, or open sync).
    advancingRef.current = false;
    return () => {
      if (advanceTimerRef.current !== null) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [currentIndex]);
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const isLastQuestion = !canGoForward;
  const selectAnswer = (score: number) => {
    // Ignore rapid re-clicks while auto-advance is pending — each click used
    // to schedule another +1 and skip unanswered questions.
    if (advancingRef.current) return;
    setAnswer(question.id, score);
    if (!canGoForward) return;
    advancingRef.current = true;
    const delay = reduceMotion ? 0 : 180;
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      setCurrentIndex(index => index + 1);
    }, delay);
  };
  const goBack = () => {
    if (!canGoBack) return;
    clearAdvanceTimer();
    setCurrentIndex(index => index - 1);
  };
  const goForward = () => {
    if (!canGoForward || selectedScore === undefined) return;
    clearAdvanceTimer();
    setCurrentIndex(index => index + 1);
  };
  const showScore = () => {
    if (!isComplete || !result) {
      const missingIndex = SCORED_QUESTIONS.findIndex(item => answers[item.id] === undefined);
      setCurrentIndex(missingIndex === -1 ? TOTAL_QUESTIONS - 1 : missingIndex);
      return;
    }
    trackScoreComplete(result.totalScore, result.band);
    // Same-tab navigation — window.open is often blocked and feels like a dead button.
    window.location.assign(createScoreResultUrl(result));
  };
  return <dialog ref={dialogRef} tabIndex={-1} aria-labelledby="diagnostic-title" className="fixed inset-0 z-[60] m-0 h-full max-h-none w-full max-w-none overflow-y-auto border-0 bg-paper p-0 open:flex open:flex-col focus:outline-none" data-id="element-126" data-mp="yepwq1lqnkn3">
      <div className="mx-auto w-full max-w-3xl flex-1 px-6 py-6 md:py-10" data-id="element-127" data-mp="17fo4muzascay">
        <div className="flex items-center justify-between gap-4" data-id="element-128" data-mp="apf3r6amquji">
          <div data-id="element-129" data-mp="aqzvko5k7zdo">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-130" data-mp="anc4dvnl7p24">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-131" data-mp="josf64ibdt6v">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" data-diagnostic-close onClick={() => dialogRef.current?.close()} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" aria-label="Close diagnostic" data-id="element-132" data-mp="3n6k04yy19cvk">
            <X className="h-5 w-5" aria-hidden="true" data-id="element-133" data-mp="3fn1d8hlo4zhp" />
          </button>
        </div>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-134" data-mp="3fl848vnl236j">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-135" data-mp="3aq14fyxfpimw" />)}
        </div>

        <div className="py-12 md:py-16" data-id="element-136" data-mp="31obfr0uxsxst">
          <p className="text-sm font-medium text-text-muted" data-id="element-137" data-mp="31pcqf481jia0">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h2 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-138" data-mp="2rwx9894kixq6">
            {question.prompt}
          </h2>

          <div className="mt-9 flex flex-col gap-3" data-id="element-139" data-mp="2mwap31eo4mi0">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-140" data-mp="2dz0dfexxuvck">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass-ink text-white' : 'border-gray-300 text-navy'}`} data-id="element-141" data-mp="rqzqhuf6uic">
                    {isSelected ? <Check className="h-4 w-4" aria-hidden="true" data-id="element-142" data-mp="396r5lkmo8wbz" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-143" data-mp="3e5kknhhlwve9">{option.text}</span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-144" data-mp="3074djpfu12vl">
            {canGoBack ? <button type="button" onClick={goBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-145" data-mp="353b8yoalqvza">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-146" data-mp="2ujiq47mby4u7" />
                Back
              </button> : <span className="inline-flex items-center gap-2 px-4 py-3 text-sm text-text-muted" aria-hidden="true" data-id="element-147" data-mp="2qgw2yk0lt83c">
                {/* Spacer keeps Next/See My Score right-aligned on Q1 */}
              </span>}

            {isLastQuestion ? <div className="flex flex-col items-end gap-2" data-id="element-148" data-mp="2zhasjvklp9cn">
                <Cta variant="primary" size="md" showArrow onClick={showScore} disabled={selectedScore === undefined || !isComplete} aria-describedby={selectedScore === undefined || !isComplete ? 'diagnostic-incomplete-hint' : undefined} data-id="element-149" data-mp="3e7i0hhczf9z3">
                  See My Score
                </Cta>
                {(selectedScore === undefined || !isComplete) && <p id="diagnostic-incomplete-hint" className="max-w-xs text-right text-sm text-text-muted" data-id="element-150" data-mp="3ia6a3r9iskx4">
                    {selectedScore === undefined ? 'Choose an answer for this question to continue.' : 'One earlier question is still unanswered. Use Back to finish it.'}
                  </p>}
              </div> : <button type="button" onClick={goForward} disabled={selectedScore === undefined} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-151" data-mp="2x977g1kh2ewy">
                Next question
                <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-152" data-mp="3fydu1zodzqo1" />
              </button>}
          </div>
        </div>
      </div>
    </dialog>;
}